// NumericInput — a numeric text field that doesn't fight the user mid-keystroke.
//
// Why this file exists: per CLAUDE.md "NO `<input type='number'>`" hard rule,
// browser type=number causes (a) controlled-value reformatting that strips
// trailing dots while typing, (b) browser-specific validation that rejects
// intermediate states like "1." or "-", and (c) scroll-to-change behavior.
//
// Pattern: type="text" + inputMode="decimal" with local string state. The
// parent's onChange still fires with the raw string (matching the existing
// Input wrapper API), so callers don't need to change.
//
// Sync semantics: local state mirrors the parent's value, but only when the
// parent's value differs from what our local string would parse to. This
// preserves the trailing-dot moment ("1.") through the echo: parent gets
// onChange("1."), parses to 1, re-passes value=1, but our local state stays
// at "1." because parseFloat("1.") === 1.

import React, { useState, useCallback, useEffect } from "react";

export interface NumericInputProps {
  value: string | number | undefined;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Soft min — clamped on blur, not while typing. */
  min?: number | undefined;
  /** Soft max — clamped on blur, not while typing. */
  max?: number | undefined;
  /** Allow a leading minus sign. Default true. */
  allowNegative?: boolean | undefined;
  /** Class names appended to the form-input base class. */
  className?: string | undefined;
  /** Optional id for label association. */
  id?: string | undefined;
  /** Optional aria-label for accessibility when no label is associated. */
  "aria-label"?: string | undefined;
  /** Optional name attribute for form submission. */
  name?: string | undefined;
  /** Optional inline style passthrough for one-off layouts. */
  style?: React.CSSProperties | undefined;
  /** Forwarded keyboard handlers for arrow-nav / form submission patterns. */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
  /** Called after the internal blur normalisation (clamp, partial-input clear). */
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
}

const VALID_NUMERIC = /^-?\d*\.?\d*$/;
const VALID_POSITIVE = /^\d*\.?\d*$/;

function valueToString(v: string | number | undefined): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (Number.isNaN(v)) return "";
  return String(v);
}

export const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  min,
  max,
  allowNegative = true,
  className = "",
  id,
  name,
  "aria-label": ariaLabel,
  style,
  onKeyDown,
  onKeyPress,
  onFocus,
  onBlur: onBlurProp,
}) => {
  const [text, setText] = useState<string>(() => valueToString(value));

  // Sync local state from parent — but ignore the echo from our own onChange
  // (e.g., user types "1.", we emit "1.", parent stores 1, re-renders with
  // value=1; we should NOT collapse "1." back to "1" mid-edit).
  useEffect(() => {
    const incoming = valueToString(value);
    const localAsNumber = parseFloat(text);
    const incomingAsNumber = parseFloat(incoming);
    // If the numbers parse to the same value, the parent is just echoing — keep
    // the user's in-progress text. Otherwise the parent changed externally.
    if (
      incoming !== text &&
      !(
        Number.isFinite(localAsNumber) &&
        Number.isFinite(incomingAsNumber) &&
        localAsNumber === incomingAsNumber
      )
    ) {
      setText(incoming);
    }
    // text intentionally not in deps — we only react to external value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const pattern = allowNegative ? VALID_NUMERIC : VALID_POSITIVE;
      if (raw !== "" && !pattern.test(raw)) return; // reject invalid chars
      setText(raw);
      // Always emit the string upward — even intermediate states like "1." or "-".
      // Parents that store as number can parseFloat; parsing "1." yields 1 (valid).
      onChange(raw);
    },
    [onChange, allowNegative],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (text === "" || text === "-" || text === "." || text === "-.") {
        // Empty / partial — emit empty string so parent can decide (commonly: clear)
        setText("");
        onChange("");
      } else {
        let n = parseFloat(text);
        if (Number.isNaN(n)) {
          setText("");
          onChange("");
        } else {
          if (typeof min === "number" && n < min) n = min;
          if (typeof max === "number" && n > max) n = max;
          const clean = String(n);
          setText(clean);
          onChange(clean);
        }
      }
      onBlurProp?.(e);
    },
    [text, onChange, min, max, onBlurProp],
  );

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      className={`form-input ${className}`.trim()}
      onChange={handleChange}
      onBlur={handleBlur}
      id={id}
      name={name}
      aria-label={ariaLabel}
      style={style}
      onKeyDown={onKeyDown}
      onKeyPress={onKeyPress}
      onFocus={onFocus}
    />
  );
};
